import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { StockEnum, StockNumericType } from "data/enums";
import { stockDetailPath } from "data/stock_types";
import { BookingDataType } from "data/interfaces";
import { selectBookings, setBookings } from "application/features/bookings/bookingsSlice";
import { selectCurrentLoggedClient } from "application/features/clientAuth/authClientSlice";
import { useSendPackageRequestMutation } from "application/features/holidayPackage/holidayPackageApiSlice";
import { buildPackageRequestPayload } from "application/utils/latestBooking";
import { useTurnstile } from "application/hooks/useTurnstile";

interface CartRequestHandlers {
  handleRemoveFromTrip: (item: BookingDataType) => void;
  handleCartSendRequest: (item: BookingDataType) => Promise<void>;
  handleChangeDates: (item: BookingDataType) => void;
  isSendingRequest: boolean;
}

/**
 * Centralized handlers for the cart's per-item actions on holiday packages
 * (remove, send request, change dates). Used by both the cart page
 * (`/my-trip`) and the checkout summary page (`/client/booking/summary`).
 */
export const useCartRequestHandlers = (): CartRequestHandlers => {
  const { t } = useTranslation("translation");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const bookings: BookingDataType[] = useSelector(selectBookings);
  const loggedClient = useSelector(selectCurrentLoggedClient);
  const [sendPackageRequest, { isLoading: isSendingRequest }] =
    useSendPackageRequestMutation();
  const { getToken: getCaptchaToken } = useTurnstile("package_request");

  const handleRemoveFromTrip = (item: BookingDataType) => {
    dispatch(setBookings(bookings.filter((b) => b.id !== item.id)));
  };

  const handleCartSendRequest = async (item: BookingDataType) => {
    if (item.type !== StockEnum.HOLIDAY_PACKAGE) return;
    const pkg = item.item;

    let captchaToken: string | null;
    try {
      captchaToken = await getCaptchaToken();
    } catch {
      toast.error(t("translation:captcha.error"));
      return;
    }

    try {
      const payload = buildPackageRequestPayload({
        packageName: pkg?.name ?? "",
        packageUuid: pkg ? String(pkg.package_uuid) : "",
        startDate: item.startDate,
        endDate: item.endDate,
        guests: item.guests ?? 0,
        // TODO: format price for cart items (mirror detail page's totalFormattedPrice)
        price: item.price,
        clientEmail: loggedClient?.email ?? "",
      });
      await sendPackageRequest({ ...payload, captcha_token: captchaToken }).unwrap();
      dispatch(setBookings(bookings.filter((b) => b.id !== item.id)));
      toast.success(t("translation:detailPages.common.requestSent"));
    } catch {
      toast.error(t("translation:detailPages.common.requestError"));
    }
  };

  const handleChangeDates = (item: BookingDataType) => {
    if (item.type !== StockEnum.HOLIDAY_PACKAGE) return;
    const pkg = item.item;
    if (!pkg?.package_uuid) return;
    dispatch(setBookings(bookings.filter((b) => b.id !== item.id)));
    navigate(stockDetailPath(StockNumericType.HOLIDAY_PACKAGE, pkg.slug || pkg.package_uuid));
  };

  return {
    handleRemoveFromTrip,
    handleCartSendRequest,
    handleChangeDates,
    isSendingRequest,
  };
};
